import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import orientationService from '../../../api/services/orientationService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import {
  ArrowLeft, Copy, Plus, Trash2, AlertCircle
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';

const CloneOrientation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useCustomAlert();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'in_person',
    location: '',
    date: '',
    time: '',
    duration: '',
    capacity: '',
    trainer: '',
    department: '',
    agenda: [],
    materials: []
  });

  const [newAgendaItem, setNewAgendaItem] = useState({ title: '', duration: '', description: '' });
  const [newMaterial, setNewMaterial] = useState({ name: '', type: '', size: '' });
  const [cloneOptions, setCloneOptions] = useState({
    includeAgenda: true,
    includeMaterials: true,
    includeAttendees: false,
    adjustSchedule: true
  });

  const departmentOptions = [
    'HR', 'IT', 'Finance', 'Marketing', 'Sales', 'Operations', 'Customer Service'
  ];

  const trainerOptions = [
    'Sarah Johnson', 'Mike Chen', 'Emily Davis', 'David Wilson', 'Lisa Brown'
  ];

  useEffect(() => {
    loadOrientation();
  }, [id, cloneOptions.includeAgenda, cloneOptions.includeMaterials]);

  const loadOrientation = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await orientationService.getOrientationById(id);

      if (result && result.responseCode === 200 && result.data) {
        const originalOrientation = result.data.orientation;

        // Set default values for cloned orientation
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split('T')[0];

        setFormData({
          title: `${originalOrientation.title} (Copy)`,
          description: originalOrientation.description,
          type: originalOrientation.type,
          location: originalOrientation.location,
          date: tomorrowStr,
          time: originalOrientation.time,
          duration: originalOrientation.duration,
          capacity: originalOrientation.capacity,
          trainer: originalOrientation.trainer,
          department: originalOrientation.department,
          agenda: cloneOptions.includeAgenda ? [...(originalOrientation.agenda || [])] : [],
          materials: cloneOptions.includeMaterials ? [...(originalOrientation.materials || [])] : []
        });
      } else {
        setError('Orientation not found');
        showError('Orientation not found');
      }
    } catch (error) {
      setError('Error loading orientation');
      showError('Error loading orientation');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Enforce max lengths and sanitization inline
    let newValue = value;

    // Text field validation - only allow alphanumeric, spaces, and commas
    if (name === 'title' && typeof newValue === 'string') {
      newValue = newValue.replace(/[^a-zA-Z0-9\s,]/g, '');
      newValue = newValue.slice(0, 100);
    }
    if (name === 'description' && typeof newValue === 'string') {
      newValue = newValue.replace(/[^a-zA-Z0-9\s,]/g, '');
      newValue = newValue.slice(0, 500);
    }
    if (name === 'location' && typeof newValue === 'string') {
      const maxLen = formData.type === 'virtual' ? 200 : 100;
      if (formData.type === 'virtual') {
        // Allow URL format but still sanitize
        newValue = newValue.slice(0, maxLen);
      } else {
        // Remove invalid characters (only allow alphanumeric, spaces, commas)
        newValue = newValue.replace(/[^a-zA-Z0-9\s,]/g, '');
        newValue = newValue.slice(0, maxLen);
      }
    }

    // Number field validation - only allow positive numbers (0 and greater)
    if (name === 'duration' && typeof newValue === 'string') {
      const numValue = Number(newValue) || 0;
      if (numValue < 0.5) newValue = 0.5;
      if (numValue > 8) newValue = 8;
    }
    if (name === 'capacity' && typeof newValue === 'string') {
      const numValue = parseInt(newValue) || 0;
      if (numValue < 0) newValue = 0;
      if (numValue > 100) newValue = 100;
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCloneOptionChange = (option) => {
    setCloneOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleAgendaAdd = () => {
    if (newAgendaItem.title.trim()) {
      const agendaItem = {
        id: Date.now(),
        title: newAgendaItem.title,
        duration: parseInt(newAgendaItem.duration),
        description: newAgendaItem.description
      };
      setFormData(prev => ({
        ...prev,
        agenda: [...prev.agenda, agendaItem]
      }));
      setNewAgendaItem({ title: '', duration: 30, description: '' });
    }
  };

  const handleAgendaRemove = (agendaId) => {
    setFormData(prev => ({
      ...prev,
      agenda: prev.agenda.filter(item => item.id !== agendaId)
    }));
  };

  const handleMaterialAdd = () => {
    if (newMaterial.name.trim()) {
      const material = {
        id: Date.now(),
        name: newMaterial.name,
        type: newMaterial.type,
        size: newMaterial.size
      };
      setFormData(prev => ({
        ...prev,
        materials: [...prev.materials, material]
      }));
      setNewMaterial({ name: '', type: 'PDF', size: '' });
    }
  };

  const handleMaterialRemove = (materialId) => {
    setFormData(prev => ({
      ...prev,
      materials: prev.materials.filter(item => item.id !== materialId)
    }));
  };

  const validateForm = async () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    else if (formData.title.length < 3) newErrors.title = 'Title must be at least 3 characters';
    else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.title)) newErrors.title = 'Title must contain only alphanumeric characters, spaces, and commas';

    if (!formData.description.trim()) newErrors.description = 'Description is required';
    else if (formData.description.length < 10) newErrors.description = 'Description must be at least 10 characters';
    else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.description)) newErrors.description = 'Description must contain only alphanumeric characters, spaces, and commas';

    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!formData.trainer) newErrors.trainer = 'Trainer is required';

    if (formData.location && formData.type === 'in_person') {
      if (!/^[a-zA-Z0-9\s,]+$/.test(formData.location)) newErrors.location = 'Location must contain only alphanumeric characters, spaces, and commas';
    }

    if (formData.capacity === '' || formData.capacity === null || formData.capacity === undefined) {
      newErrors.capacity = 'Capacity is required';
    } else if (parseInt(formData.capacity) < 0) {
      newErrors.capacity = 'Capacity must be 0 or greater';
    }

    if (formData.duration === '' || formData.duration === null || formData.duration === undefined) {
      newErrors.duration = 'Duration is required';
    } else if (Number(formData.duration) < 0.5) {
      newErrors.duration = 'Duration must be 0.5 or greater';
    }

    // Check for duplicate orientation (same date, time, location, and duration)
    if (formData.date && formData.time && formData.location && formData.duration) {
      try {
        const result = await orientationService.checkDuplicateOrientation({
          date: formData.date,
          time: formData.time,
          location: formData.location,
          duration: formData.duration
        });

        if (result && result.responseCode === 200 && result.data.isDuplicate) {
          newErrors.duplicate = 'An orientation already exists at the same date, time, location, and duration. Please choose different details.';
        }
      } catch (error) {
        // Don't block form submission if duplicate check fails
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!(await validateForm())) {
      showError('Please fix the validation errors before submitting. All required fields must be filled correctly.');
      return;
    }

    // Check if all required fields are filled
    const requiredFields = ['title', 'description', 'date', 'time', 'trainer', 'duration', 'capacity'];
    const missingFields = requiredFields.filter(field => {
      if (field === 'duration' || field === 'capacity') {
        return formData[field] === '' || formData[field] === null || formData[field] === undefined;
      }
      return !formData[field] || formData[field].toString().trim() === '';
    });

    if (missingFields.length > 0) {
      showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setSaving(true);

    try {
      const cloneData = {
        title: formData.title,
        date: formData.date,
        includeAgenda: cloneOptions.includeAgenda,
        includeMaterials: cloneOptions.includeMaterials,
        includeAttendees: cloneOptions.includeAttendees,
        adjustSchedule: cloneOptions.adjustSchedule
      };

      const result = await orientationService.cloneOrientation(id, cloneData);

      if (result && result.responseCode === 201) {
        showSuccess('Orientation cloned successfully');
        navigate('/orientation');
      } else if (result && result.responseCode === 409) {
        showError(result?.responseMessage || 'An orientation already exists at the same date, time, and location. Please choose different details.');
      } else {
        showError('Failed to clone orientation');
      }
    } catch (error) {
      showError('Error cloning orientation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orientation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Orientation</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <PermissionGate permission="onboarding" action="read">
            <Button onClick={loadOrientation} className="bg-agrosaf-green hover:bg-agrosaf-darkGreen">
              Try Again
            </Button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/orientation')}
        >
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clone Orientation Session</h1>
          <p className="text-gray-600 mt-1">Create a copy of an existing orientation session</p>
        </div>
      </div>

      {/* Clone Options */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Clone Options</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cloneOptions.includeAgenda}
              onChange={() => handleCloneOptionChange('includeAgenda')}
              className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Include Agenda</span>
              <p className="text-sm text-gray-500">Copy all agenda items from the original session</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cloneOptions.includeMaterials}
              onChange={() => handleCloneOptionChange('includeMaterials')}
              className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Include Materials</span>
              <p className="text-sm text-gray-500">Copy all session materials and resources</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cloneOptions.includeAttendees}
              onChange={() => handleCloneOptionChange('includeAttendees')}
              className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Include Attendees</span>
              <p className="text-sm text-gray-500">Copy the attendee list from the original session</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cloneOptions.adjustSchedule}
              onChange={() => handleCloneOptionChange('adjustSchedule')}
              className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Adjust Schedule</span>
              <p className="text-sm text-gray-500">Automatically adjust date to tomorrow</p>
            </div>
          </label>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Session Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  // Only allow alphanumeric, spaces, and commas
                  if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter session title"
                required
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                Session Type *
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              >
                <option value="in_person">In-Person</option>
                <option value="virtual">Virtual</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  // Only allow alphanumeric, spaces, and commas
                  if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                rows={3}
                maxLength={500}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter session description"
                required
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>
          </div>
        </div>

        {/* Schedule & Location */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Schedule & Location</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                Date *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.date ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
                Time *
              </label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.time ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {errors.time && <p className="text-red-500 text-sm mt-1">{errors.time}</p>}
            </div>

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-2">
                Duration (hours) *
              </label>
              <select
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={(e) => {
                  const val = Number(e.target.value) || 0.5;
                  handleInputChange({ target: { name: 'duration', value: val } });
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.duration ? 'border-red-500' : 'border-gray-300'
                  }`}
                required
              >
                <option value="">Select Duration</option>
                <option value="0.5">30 min</option>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((hour) => (
                  <option key={hour} value={hour}>
                    {hour} {hour === 1 ? 'hour' : 'hours'}
                  </option>
                ))}
              </select>
              {errors.duration && <p className="text-red-500 text-sm mt-1">{errors.duration}</p>}
            </div>

            <div>
              <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-2">
                Capacity *
              </label>
              <input
                type="number"
                id="capacity"
                name="capacity"
                value={formData.capacity}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  if (val >= 0 && val <= 100) {
                    handleInputChange({ target: { name: 'capacity', value: val } });
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent negative sign and decimal point
                  if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                min="0"
                max="100"
                step="1"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.capacity ? 'border-red-500' : 'border-gray-300'
                  }`}
                required
              />
              {errors.capacity && <p className="text-red-500 text-sm mt-1">{errors.capacity}</p>}
            </div>

            {formData.type === 'in_person' && (
              <div className="md:col-span-2">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  onKeyPress={(e) => {
                    // For virtual, allow URL characters; for in-person, only alphanumeric, spaces, commas
                    if (formData.type === 'virtual') {
                      // Allow URL format characters
                      if (!/^[a-zA-Z0-9\s,.\-:/]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    } else {
                      // Only allow alphanumeric, spaces, and commas
                      if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }
                  }}
                  maxLength={formData.type === 'virtual' ? 200 : 100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  placeholder="Enter location"
                />
              </div>
            )}
          </div>
        </div>

        {/* Trainer & Department */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Trainer & Department</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="trainer" className="block text-sm font-medium text-gray-700 mb-2">
                Trainer *
              </label>
              <select
                id="trainer"
                name="trainer"
                value={formData.trainer}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.trainer ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="">Select trainer</option>
                {trainerOptions.map(trainer => (
                  <option key={trainer} value={trainer}>{trainer}</option>
                ))}
              </select>
              {errors.trainer && <p className="text-red-500 text-sm mt-1">{errors.trainer}</p>}
            </div>

            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <select
                id="department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              >
                {departmentOptions.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Agenda */}
        {cloneOptions.includeAgenda && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Agenda</h2>

            <div className="space-y-4">
              {formData.agenda.map((item, index) => (
                <div key={item.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.description}</p>
                    <p className="text-sm text-gray-500">Duration: {item.duration} minutes</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAgendaRemove(item.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={newAgendaItem.title}
                      onChange={(e) => {
                        // Only allow alphanumeric, spaces, and commas
                        const sanitized = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '');
                        setNewAgendaItem(prev => ({ ...prev, title: sanitized.slice(0, 100) }));
                      }}
                      onKeyPress={(e) => {
                        // Only allow alphanumeric, spaces, and commas
                        if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      maxLength={100}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      placeholder="Agenda item title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration (min)</label>
                    <input
                      type="number"
                      value={newAgendaItem.duration}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        if (val >= 0 && val <= 120) {
                          setNewAgendaItem(prev => ({ ...prev, duration: val }));
                        }
                      }}
                      onKeyDown={(e) => {
                        // Prevent negative sign and decimal point
                        if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }}
                      min="0"
                      max="120"
                      step="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={handleAgendaAdd}
                      disabled={!newAgendaItem.title.trim()}
                    >
                      Add Item
                    </Button>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newAgendaItem.description}
                    onChange={(e) => {
                      // Only allow alphanumeric, spaces, and commas
                      const sanitized = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '');
                      setNewAgendaItem(prev => ({ ...prev, description: sanitized.slice(0, 200) }));
                    }}
                    onKeyPress={(e) => {
                      // Only allow alphanumeric, spaces, and commas
                      if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    rows={2}
                    maxLength={200}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    placeholder="Agenda item description"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Error */}
        {errors.duplicate && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Duplicate Orientation Detected</h3>
                <p className="text-sm text-red-700 mt-1">{errors.duplicate}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/orientation')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<Copy className="h-4 w-4" />}
            loading={saving}
          >
            {saving ? 'Creating Clone...' : 'Create Clone'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CloneOrientation;
